

import { Button } from '@repo/ui/components/ui/button';
import { useRouter } from '@repo/ui/hooks/use-router';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useInventoryData } from '../../../../hooks/use-inventory-data';
import { usePrintData } from '../../hooks/use-print-data';
import InterventionForm from './components/InterventionForm';
import { usePDFDownload } from '../../print/hooks/use-pdf-download';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { pdfStyles } from '../../print/shared';
import { getDynamicFieldOptions } from '../../shared/components/dynamic-field-options';
import { ARBRE_FIELDS_PRINT_CONFIG } from '@repo/field-configs';
import { formatDate } from '../../epaysage/print/utils/format-utils';
import { imageService } from '@repo/ui/lib/services';
import { useUserSettings } from '../../../../hooks/api/user-settings';

interface InterventionArbreProps {
  inventaireId: number;
}

export default function InterventionArbre({ inventaireId }: InterventionArbreProps) {
  // #region agent log

  // #endregion
  const router = useRouter();
  // #region agent log

  // #endregion
  const { especes, champignons, nuisibles } = useInventoryData(false);
  // #region agent log

  // #endregion
  const { data: userSettingsData } = useUserSettings();
  // #region agent log

  // #endregion
  const [printMode, setPrintMode] = useState(false);

  // Fetch print data from API (formatted with all replacements) - SINGLE SOURCE OF TRUTH
  const { data: printData, isLoading, error } = usePrintData(inventaireId);

  // Load inventory data when component mounts
  useEffect(() => {
    if (especes.length === 0 || champignons.length === 0 || nuisibles.length === 0) {
      // Trigger data loading if not already loaded
      // This will be handled by the useInventoryData hook
    }
  }, [especes, champignons, nuisibles]);

  const handlePrint = () => {
    setPrintMode(true);

    // Ensure all content is loaded and visible
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        // Force scroll to top to ensure all content is rendered
        window.scrollTo(0, 0);

        // Additional delay to ensure all content is rendered
        setTimeout(() => {
          window.print();
          setPrintMode(false);
        }, 200);
      } else {
        setPrintMode(false);
      }
    }, 300);
  };

  const handleBack = () => {
    router.back();
  };

  // Values from print-data API are already formatted strings, just return them as-is
  const getDynamicFieldValue = (field: string, value: any) => {
    if (!value) return "";
    return typeof value === 'string' ? value : String(value);
  };

  // Parse GPS coordinates from print-data format (string "lat, lng") or object format
  const parseGPS = (gps: string | { lat: number; lng: number } | undefined): { lat: number; lng: number } | undefined => {
    if (!gps) return undefined;
    if (typeof gps === 'object' && 'lat' in gps && 'lng' in gps) {
      return gps;
    }
    if (typeof gps === 'string') {
      const parts = gps.split(',').map(p => p.trim()).filter(p => p.length > 0);
      if (parts.length >= 2) {
        const latStr: string | undefined = parts[0];
        const lngStr: string | undefined = parts[1];
        if (latStr !== undefined && lngStr !== undefined) {
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);
          if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
          }
        }
      }
    }
    return undefined;
  };

  // Generate PDF function - creates intervention PDF using printData (same layout as InterventionForm)
  const generatePDF = useMemo(() => {
    return async (): Promise<Blob> => {
      if (!printData) {
        throw new Error('Données non disponibles pour la génération du PDF');
      }

      try {
        // Parse GPS coordinates from print-data format (string "lat, lng")
        const coord = parseGPS(printData.gps);

        // Generate static map images using Leaflet and leaflet-image (3 versions: OSM, Google Maps, Satellite)
        let staticMapsBase64: { openstreetmap?: string; google?: string; satellite?: string } | null = null;
        if (coord && typeof coord === 'object' && 'lat' in coord && 'lng' in coord) {
          try {
            const lat = coord.lat;
            const lng = coord.lng;

            // Dynamically import Leaflet and leaflet-image
            const LModule = await import('leaflet');
            // @ts-ignore
            await import('leaflet/dist/leaflet.css');
            // @ts-ignore - leaflet-image doesn't have TypeScript types
            const leafletImageModule = await import('leaflet-image');
            const leafletImage = leafletImageModule.default || leafletImageModule;

            // Extract Leaflet from module (handle both default and named exports)
            // @ts-ignore - Leaflet typing issue with dynamic import
            const L = LModule.default || LModule;

            // Fix Leaflet default icons
            // @ts-ignore
            delete L.Icon.Default.prototype._getIconUrl;
            // @ts-ignore
            L.Icon.Default.mergeOptions({
              iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
              iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
              shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
            });

            // Helper function to generate a map image
            const generateMapImage = async (tileUrl: string, subdomains?: string[]): Promise<string> => {
              // Create a temporary container for the map (hidden)
              const mapContainer = document.createElement('div');
              mapContainer.style.width = '400px';
              mapContainer.style.height = '300px';
              mapContainer.style.position = 'absolute';
              mapContainer.style.left = '-9999px';
              mapContainer.style.top = '-9999px';
              document.body.appendChild(mapContainer);

              // Create Leaflet map
              // @ts-ignore
              const map = L.map(mapContainer, {
                center: [lat, lng],
                zoom: 16,
                zoomControl: false,
                attributionControl: false,
                scrollWheelZoom: false,
                dragging: false,
                doubleClickZoom: false,
                boxZoom: false,
                keyboard: false,
                touchZoom: false,
              });

              // Add tile layer
              // @ts-ignore
              const tileLayer = L.tileLayer(tileUrl, {
                attribution: '',
                maxZoom: 19,
                subdomains: subdomains || ['a', 'b', 'c'],
              });
              tileLayer.addTo(map);

              // Add marker for the location
              // @ts-ignore
              const marker = L.marker([lat, lng], {
                // @ts-ignore
                icon: L.icon({
                  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
                  iconSize: [25, 41],
                  iconAnchor: [12, 41],
                  popupAnchor: [1, -34],
                  shadowSize: [41, 41],
                }),
              }).addTo(map);

              // Wait for map tiles to load using 'idle' event
              await new Promise<void>((resolve) => {
                map.whenReady(() => {
                  // Wait for tiles to load
                  map.once('idle', () => {
                    setTimeout(resolve, 500); // Small delay to ensure rendering
                  });
                });

                // Timeout fallback
                setTimeout(resolve, 3000);
              });

              // Capture map as image using leaflet-image (callback-based API)
              // @ts-ignore - leaflet-image doesn't have TypeScript types
              const imageBase64 = await new Promise<string>((resolve, reject) => {
                leafletImage(map, (err: Error | null, canvas: HTMLCanvasElement | null) => {
                  if (err || !canvas) {
                    reject(err || new Error('Failed to capture map image'));
                    return;
                  }

                  // Convert canvas to Base64
                  const dataUrl = canvas.toDataURL('image/png', 0.95);
                  resolve(dataUrl);
                });
              });

              // Cleanup: remove map and container
              map.remove();
              document.body.removeChild(mapContainer);

              return imageBase64;
            };

            // Generate all three map versions
            staticMapsBase64 = {
              openstreetmap: await generateMapImage('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
              google: await generateMapImage('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']),
              satellite: await generateMapImage('https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', ['mt0', 'mt1', 'mt2', 'mt3']),
            };
          } catch (mapError) {
            console.warn('Erreur lors de la génération des cartes avec Leaflet:', mapError);
            // Continue without map images if it fails to load
          }
        }

        // Import the PDF generation function
        const { generateInterventionPDFDocument } = await import('./generate-intervention-pdf');

        // Create PDF document using intervention-specific layout
        const PDFDocumentComponent = generateInterventionPDFDocument({
          printData,
          inventaireId,
          userSettingsData,
          staticMapsBase64: staticMapsBase64 || undefined, // Pass the Base64 images (convert null to undefined)
        });

        const blob = await pdf(PDFDocumentComponent).toBlob();

        if (!blob) {
          throw new Error('Impossible de générer le blob PDF');
        }

        return blob;
      } catch (error) {
        console.error('Erreur lors de la génération du PDF:', error);
        throw error instanceof Error ? error : new Error('Erreur inconnue lors de la génération du PDF');
      }
    };
  }, [printData, userSettingsData, parseGPS]);

  const { isGeneratingPDF, handleDownloadPDF } = usePDFDownload(generatePDF, {
    filename: `intervention-${inventaireId || 'fiche'}.pdf`,
    onError: (error) => {
      console.error('Erreur génération PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error || !printData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erreur</h1>
          <p className="text-muted-foreground mb-4">
            Impossible de charger les données de l'inventaire
          </p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white ${printMode ? 'print-mode' : 'min-h-screen'}`}>
      {/* Print Controls - Hidden when printing */}
      <div className="fixed top-4 right-4 print:hidden z-50 flex gap-2">

        <Button onClick={handleDownloadPDF} disabled={isGeneratingPDF || !printData} className="gap-2">
          <Download className="h-4 w-4" />
          {isGeneratingPDF ? 'Génération...' : 'Télécharger PDF'}
        </Button>
      </div>

      {/* Main Intervention Content */}
      <InterventionForm printData={printData} inventaireId={inventaireId} />


    </div>
  );
}
